import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { LeadService } from 'app/global-services/lead.service';
import { MediaService } from 'app/global-services/media.service';
import { FolderService } from 'app/global-services/folder.service';
import { isValidEmail, isValidPhoneNumber, truncateField } from 'app/shared/utils';
import { Router } from '@angular/router';
import { finalize , catchError, of, switchMap, Observable, map } from 'rxjs';
import { COMPANY_MAX_LENGTH, DESIGNATION_MAX_LENGTH, EMAIL_MAX_LENGTH, FIRST_NAME_MAX_LENGTH, LAST_NAME_MAX_LENGTH, LEAD_FILENAME_PREFIX, PHONE_MAX_LENGTH } from 'app/app.constants';
import { parsePhoneNumber } from 'libphonenumber-js';

// Handling the initial state of the "Create Lead" flow:
// - "Edit" state: Default state where the user can input or edit lead details.
// - "Parsing" state: Skeleton loader is displayed while the image is being processed.
// - "Error Parsing" state: Triggered when an error occurs during image summarization, e.g., invalid image format.
enum LEAD_STATE {
    EDIT = 'edit',
    PARSING = 'parsing',
    ERROR_PARSING = 'error-parsing',
}

// Handling the state of the image processing status:
// - "Default" state: The initial state when no visual changes are needed on the UI, indicating no ongoing processing or updates.
// - "Success" state: Triggered when the image is successfully processed or parsed.
// - "Invalid" state: Triggered when an error occurs during image summarization, e.g., invalid image format.
enum AFTER_IMAGE_PROCESSING {
    DEFAULT = 'default',
    SUCCESS = 'success',
    INVALID = 'invalid',
}

// Handling the state of the "Add" lead create button:
// - "Default" state: The initial state when no visual changes are needed on the UI, indicating no ongoing saving or updates.
// - "Saving" state: Triggered when the contact details are being saved or submitted, a spinner will be shown in this state.
// - "Success" state: Triggered when the contact details are successfully saved.
// - "Error" state: Triggered when an error occurs during saving the contact details.
enum LEAD_SAVE_CONTACT_STATE {
    DEFAULT = 'default',
    SAVING = 'saving',
    SUCCESS = 'success',
    ERROR = 'error',
}

enum CONTACT_FIELD_FORM {
    FIRST_NAME = 'first_name',
    LAST_NAME = 'last_name',
    EMAIL = 'email',
    PHONE = 'phone',
    DESIGNATION = 'designation',
    COMPANY = 'company',
}

let ERROR_MESSAGE_FORM = {
    'first_name' : '',
    'last_name' : '',
    'email': '',
    'phone': '',
    'designation': '',
    'company': '',
}

interface PotentialLeadUserType {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    designation: string;
    company: string;
}

@Component({
    selector: 'app-image-capture-create-lead',
    templateUrl: './image-capture-create-lead.component.html',
    styleUrls: ['./image-capture-create-lead.component.scss'],
    standalone: false
})
export class ImageCaptureCreateLeadComponent {
    @Input() capturedImage: File;
    @Output() closeScreen = new EventEmitter();

    isValidEmail = isValidEmail;

    LEAD_STATE = LEAD_STATE;
    LEAD_SAVE_CONTACT_STATE = LEAD_SAVE_CONTACT_STATE;
    AFTER_IMAGE_PROCESSING = AFTER_IMAGE_PROCESSING;
    CONTACT_FIELD_FORM = CONTACT_FIELD_FORM;
    ERROR_MESSAGE_FORM = ERROR_MESSAGE_FORM;

    // current state of lead capture button
    current_lead_state = LEAD_STATE.EDIT;
    save_contact_state = LEAD_SAVE_CONTACT_STATE.DEFAULT;
    image_processing_state = AFTER_IMAGE_PROCESSING.DEFAULT;

    // Store Contact Form Data
    businessCardLeadFieldForm: FormGroup;

    // fields for user lead
    userLeadFields = [
        'first_name',
        'last_name',
        'email',
        'phone',
        'designation',
        'company',
    ];

    // form fields state controller
    isFieldDisable: boolean = true;

    processingUploadImage: boolean = false;
    tempCapturedURLData: string | ArrayBuffer | null = null;

    // Capture image url stored in s3
    capture_url_stored_in_S3: string = '';

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly leadService: LeadService,
        private readonly mediaService: MediaService,
        private readonly folderService: FolderService,
        private readonly router: Router
    ) {
        this.businessCardLeadFieldForm = this.formBuilder.group({
            first_name: ['',[Validators.required]],
            last_name: ['',[Validators.required]],
            email: ['',[Validators.required, Validators.maxLength(EMAIL_MAX_LENGTH), this.isValidLeadEmail]],
            phone: ['',[Validators.maxLength(PHONE_MAX_LENGTH),this.isValidLeadPhoneNumber]],
            designation: [''],
            company: [''],
        });
    }

    // Validating Email and phone number
    isValidLeadEmail(control: AbstractControl): ValidationErrors | null {
        return isValidEmail(control.value) ? null : { invalidEmail: true}
    }

    isValidLeadPhoneNumber(control: AbstractControl): ValidationErrors | null {
        let phoneNumber:string = control.value;
        // Add try-catch to handle cases where the user doesn't include the country code.
        // if parsePhoneNumber not able to find country code then it will try error of INVALID_COUNTRY
        // This prevents errors and allows us to check the phone number without the country code in the final custom function.
        try {
            parsePhoneNumber(phoneNumber);
            return null;
        } catch(e) {}
        return phoneNumber.length === 0 || isValidPhoneNumber(phoneNumber) ? null : { invalidPhoneNumber: true};
    }

    ngOnInit() {
        this.updateLeadImage(this.capturedImage);
        this.uploadImage(this.capturedImage);
        this.subscribeToFieldChanges();
    }

    // Subscribe to all field for checking error state
    private subscribeToFieldChanges(): void {
        Object.keys(this.businessCardLeadFieldForm.controls).forEach(fieldName => {
            this.businessCardLeadFieldForm.get(fieldName)?.valueChanges.subscribe(() => {
                this.getErrorMessageForFormField(fieldName);
            });
        });
    }

    // Get the hidden folder id for storing lead
    private getLeadAssetsFolder(): Observable<any> {
        return this.folderService.getHiddenLeadFolderDetail().pipe(
            // If successful, map the response and assign the folder id
            map((response) => {
                if(!response) {
                    console.error('Error getting hidden lead folder');
                }
                return response?.id;
            }),
            catchError((error) => {
                console.error('Error getting hidden lead folder:', error);
                return null;
            })
        );
    }

    // Update the state of lead state
    private handleLeadState(state: LEAD_STATE) {
        this.current_lead_state = state;
    }

    // Update the state of image processing status
    private handleAfterImageProcessingState(state: AFTER_IMAGE_PROCESSING) {
        this.image_processing_state = state;
        setTimeout(() => {
            this.image_processing_state = AFTER_IMAGE_PROCESSING.DEFAULT;
        }, 3000);
    }

    // Update the state of 'Add' lead create button
    private handleSaveContactState(state: LEAD_SAVE_CONTACT_STATE) {
        this.save_contact_state = state;
    }

    private fetchAllValueFromLeadInputField() {
        let lead_obj: PotentialLeadUserType = {
            first_name: '',
            last_name: '',
            email: '',
            phone: '',
            designation: '',
            company: '',
        };
        this.userLeadFields.forEach((item) => {
            switch(item) {
                case CONTACT_FIELD_FORM.FIRST_NAME: {
                    lead_obj[item] = truncateField(this.businessCardLeadFieldForm.get(CONTACT_FIELD_FORM.FIRST_NAME)?.value,FIRST_NAME_MAX_LENGTH);
                    break;
                }
                case CONTACT_FIELD_FORM.LAST_NAME: {
                    lead_obj[item] = truncateField(this.businessCardLeadFieldForm.get(CONTACT_FIELD_FORM.LAST_NAME)?.value,LAST_NAME_MAX_LENGTH);
                    break;
                }
                case CONTACT_FIELD_FORM.COMPANY: {
                    lead_obj[item] = truncateField(this.businessCardLeadFieldForm.get(CONTACT_FIELD_FORM.COMPANY)?.value,COMPANY_MAX_LENGTH);
                    break;
                }
                case CONTACT_FIELD_FORM.DESIGNATION: {
                    lead_obj[item] = truncateField(this.businessCardLeadFieldForm.get(CONTACT_FIELD_FORM.DESIGNATION)?.value,DESIGNATION_MAX_LENGTH);
                    break;
                }
                case CONTACT_FIELD_FORM.PHONE: {
                    lead_obj[item] = this.businessCardLeadFieldForm.get(CONTACT_FIELD_FORM.PHONE)?.value.replace(/\s+/g, '');
                    break;
                }
                default: {
                    lead_obj[item] = this.businessCardLeadFieldForm.get(item)?.value;
                }
            }
        });
        return lead_obj;
    }

    // Store image in temp variable for preview
    private updateLeadImage(passedLeadImage: File) {
        const reader = new FileReader();
        reader.onload = () => { this.tempCapturedURLData = reader.result; };
        reader.readAsDataURL(passedLeadImage);
    }

    handleRetakeImage() {
        this.closeScreen.emit();
    }

    handleEnterDetails() {
        this.handleLeadState(LEAD_STATE.EDIT);
    }

    private uploadImage(file: File) {
        this.handleLeadState(LEAD_STATE.PARSING);

        // Getting hidden folder ID and Store Image to S3 and image ref is store inside that hidden folder
        this.getLeadAssetsFolder().pipe(
            switchMap((folderId) => this.uploadAndSummarizeImage(file, folderId)),
            catchError(this.handleUploadError),
        ).subscribe((response) => this.handleSummarizeResponse(response));
    }

    // Upload image to S3 and summarize the image
    private uploadAndSummarizeImage(file: any, folderId: number | null) {
        const EPOCH_TIME = new Date().getTime();
        const fileName = `${LEAD_FILENAME_PREFIX}${EPOCH_TIME}`;

        return this.mediaService.uploadImageToS3(file, fileName, folderId).pipe(
            switchMap((response) => {
                // If media is uploaded to S3 then summarizing the image from the URL
                if(response?.url) {
                    this.capture_url_stored_in_S3 = response['url'];
                    return this.leadService.summarizeImageFromURL(this.capture_url_stored_in_S3).pipe(
                        catchError((error) => {
                            console.error('Error uploading image for summarization:', error);
                            return of(null);
                        })
                    );
                }

                // For any case if the media is not uploaded to S3 then directly uploading the image to summarization
                // 400 status code signify that media is not uploaded to S3
                // Case 1 -> User having viewer are forbidden to upload media to S3
                // Case 2 -> Issue with puting image url to S3 bucket
                return this.leadService.summarizeImageFromFile(file).pipe(
                    catchError((error) => {
                        console.error('Error uploading image for summarization:', error);
                        return of(null);
                    })
                );
            }),
            catchError((error) => {
                console.error('Error uploading image to S3:', error);
                return of(null);
            })
        );
    }

    // Handle error during image upload and summarize of image
    private handleUploadError(error: any) {
        console.error('An error occurred:', error);
        this.handleLeadState(LEAD_STATE.ERROR_PARSING);
        return of(null);
    }

    // Handle response after image summarization
    private handleSummarizeResponse(response: any) {
        try {
            const dataResponse = response;
            this.businessCardLeadFieldForm.patchValue({
                first_name: dataResponse['first_name'],
                last_name: dataResponse['last_name'],
                email: dataResponse['email'],
                phone: dataResponse['phone'],
                designation: dataResponse['designation'],
                company: dataResponse['company'],
            });

            if (this.isAllFieldsEmpty()) {
                this.handleLeadState(LEAD_STATE.ERROR_PARSING);
                return;
            }

            this.handleLeadState(LEAD_STATE.EDIT);
            this.handleAfterImageProcessingState(AFTER_IMAGE_PROCESSING.SUCCESS);
        } catch (error) {
            console.error('Error parsing response:', error);
            this.handleLeadState(LEAD_STATE.ERROR_PARSING);
        }
    }

    // Check if all fields are empty
    private isAllFieldsEmpty() {
        return Object.values(this.businessCardLeadFieldForm.controls)
            .every(control => !control.value || control.value.trim() == '');
    }

    // Submit lead button for capturing leads on contact manager of customer dashboard
    submitLead() {
        const current_lead_obj = this.fetchAllValueFromLeadInputField();
        this.handleSaveContactState(LEAD_SAVE_CONTACT_STATE.SAVING);

        // 2 signify request is called from DBC PWA
        current_lead_obj['creation_source'] = 2;

        // Store capture image url to lead object
        current_lead_obj['capture_url'] = this.capture_url_stored_in_S3;

        // POST: request to store lead object in the server using lead service
        this.leadService.createLead(current_lead_obj).pipe(
            switchMap((response) => {
                if (!response?.id) {
                    // Update the state of lead save button to error status to show error alert message
                    this.handleAfterImageProcessingState(AFTER_IMAGE_PROCESSING.INVALID);
                    this.handleSaveContactState(LEAD_SAVE_CONTACT_STATE.ERROR);
                    return of(null);
                }

                // Update the state of lead save button to success status to show success alert message
                this.handleSaveContactState(LEAD_SAVE_CONTACT_STATE.SUCCESS);

                // Redirect to lead list after 1 second delay to show alert message
                setTimeout(() => {
                    this.router.navigate(['/cards'], {
                        fragment: 'lead_list',
                        skipLocationChange: false,
                        queryParams: { lead_id: response['id'] },
                    });
                }, 1000);

                return of(response);
            }),
            catchError((error) => {
                // Update the state of lead save button to error status to show error alert message
                console.error('Error uploading lead:', error);
                this.handleAfterImageProcessingState(AFTER_IMAGE_PROCESSING.INVALID);
                this.handleSaveContactState(LEAD_SAVE_CONTACT_STATE.ERROR);
                return of(null);
            }),
            finalize(() => {
                // Update the state of lead save button to ideal status after 3 seconds after the alert message is shown
                setTimeout(()=> {
                    this.handleSaveContactState(LEAD_SAVE_CONTACT_STATE.DEFAULT);
                },3000);
            })
        ).subscribe();
    }

    // Response the error message if field is invalid
    getErrorMessageForFormField(fieldName: string): void {
        switch (fieldName) {
            case CONTACT_FIELD_FORM.FIRST_NAME: {
                this.handleFormError(CONTACT_FIELD_FORM.FIRST_NAME, this.businessCardLeadFieldForm.get(CONTACT_FIELD_FORM.FIRST_NAME), {
                    required: 'First Name is required'
                });
                break;
            }
            case CONTACT_FIELD_FORM.LAST_NAME: {
                this.handleFormError(CONTACT_FIELD_FORM.LAST_NAME, this.businessCardLeadFieldForm.get(CONTACT_FIELD_FORM.LAST_NAME), {
                    required: 'Last Name is required'
                });
                break;
            }
            case CONTACT_FIELD_FORM.EMAIL: {
                this.handleFormError(CONTACT_FIELD_FORM.EMAIL, this.businessCardLeadFieldForm.get(CONTACT_FIELD_FORM.EMAIL), {
                    required: 'Email is required',
                    invalidEmail: 'Invalid email address',
                    maxlength: `Email can't exceed ${EMAIL_MAX_LENGTH} characters`
                });
                break;
            }
            case CONTACT_FIELD_FORM.PHONE: {
                this.handleFormError(CONTACT_FIELD_FORM.PHONE, this.businessCardLeadFieldForm.get(CONTACT_FIELD_FORM.PHONE), {
                    invalidPhoneNumber: 'Please enter correct contact number',
                    maxlength: `Phone number can't exceed ${PHONE_MAX_LENGTH} characters`
                });
                break;
            }
        }
    }

    private handleFormError(field: string, control: AbstractControl | null, errorMessages: { [key: string]: string }) {
        if (!control) return;
        const errors = control.errors;
        if (!errors) {
            // To remove the error span on UI
            delete ERROR_MESSAGE_FORM[field];
            return;
        }

        for (const [errorKey, message] of Object.entries(errorMessages)) {
            if (errors[errorKey]) {
                ERROR_MESSAGE_FORM[field] = message;
                return;
            }
        }
        // To remove the error span on UI
        delete ERROR_MESSAGE_FORM[field];
    }
}
